import { useEffect, useMemo, useRef, useState } from "react";
import { AddToHomeBanner } from "./components/AddToHomeBanner";
import { BottomTabBar } from "./components/BottomTabBar";
import { CalendarView } from "./components/CalendarView";
import { CommandCenter } from "./components/command/CommandCenter";
import { FilterBar } from "./components/FilterBar";
import { GoalsView } from "./components/GoalsView";
import { GlobalSearchPanel } from "./components/GlobalSearch";
import { InboxView } from "./components/InboxView";
import { KnowledgeView } from "./components/KnowledgeView";
import { LaneBoard } from "./components/LaneBoard";
import { MobileDrawer } from "./components/MobileDrawer";
import { MobileHeader } from "./components/MobileHeader";
import { SearchBox } from "./components/SearchBox";
import { SettingsModal } from "./components/SettingsModal";
import { ShortcutHintOverlay } from "./components/ShortcutHintOverlay";
import { Sidebar } from "./components/Sidebar";
import { StatusBar } from "./components/StatusBar";
import { TaskEditorModal } from "./components/TaskEditorModal";
import { TaskList } from "./components/TaskList";
import { Toast } from "./components/ui/Toast";
import { TopHeader } from "./components/TopHeader";
import { VoiceCaptureSheet } from "./components/VoiceCaptureSheet";
import { useTheme } from "./hooks/useTheme";
import { warmup as aiWarmup } from "./lib/ai-pwa";
import { api, isTauriEnvironment } from "./lib/tauri";
import { classificationKey, prefetchClassification } from "./lib/classify-cache";
import { useFlowShortcuts } from "./lib/shortcuts";
import { useSyncStore } from "./stores/syncStore";
import { useTaskStore } from "./stores/taskStore";
import { useUiStore } from "./stores/uiStore";
import { useKpiStore } from "./stores/kpiStore";

export default function App() {
  useTheme();
  useFlowShortcuts();
  const loadTasks = useTaskStore((s) => s.load);
  const loadSync = useSyncStore((s) => s.load);
  const loadKpi = useKpiStore((s) => s.load);
  const toast = useUiStore((s) => s.toast);
  const showToast = useUiStore((s) => s.showToast);
  const undoDelete = useTaskStore((s) => s.undoDelete);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const filter = useTaskStore((s) => s.filter);
  const appView = useUiStore((s) => s.appView);
  const allTasks = useTaskStore((s) => s.allTasks);

  // 未完了タスク。CommandCenter と同じ導出ロジック。
  const activeTasks = useMemo(
    () => allTasks.filter((t) => !t.completed && !t.deleted),
    [allTasks]
  );
  // 先読み(プリフェッチ)を App 最上位で常時駆動する。
  // どのビュー(tasks / 司令塔)にいてもタスクが変われば裏で分類を先読みし、
  // 押下時にキャッシュヒットで即表示できるようにする。
  const prefetchKey = classificationKey(activeTasks);

  // Close drawer when filter changes (user tapped a filter inside drawer)
  useEffect(() => {
    setDrawerOpen(false);
  }, [filter]);

  useEffect(() => {
    void (async () => {
      await loadTasks();
      await loadSync();
      // PWA(非Tauri)のみ起動時に1回 D1 full-pull を走らせ、クラウドの最新を取り込む。
      // Tauri/デスクトップは Rust 側に同期ループがあるため、ここでは起動しない（二重起動防止）。
      if (!isTauriEnvironment()) {
        void useSyncStore.getState().syncNow();
      }
    })();
    // Ollama モデルをバックグラウンドでウォームアップ（失敗は無視）
    void aiWarmup();
  }, [loadTasks, loadSync]);

  // ローカルHTTP API 経由（Claude Code / Codex / flow CLI）の書き込みを画面に即反映する
  useEffect(() => {
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://tasks-changed", () => {
        void loadTasks();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [loadTasks]);

  // 習慣の当日分保証: 起動時(Rust側でも実行済み) + 日付が変わったら再実行。
  // PWA 等の未対応環境は catch で無視（設計判断11）。
  // 日付変化時に kpiStore も再ロードして「今日」を更新する（設計判断: 最小対応）。
  useEffect(() => {
    let lastDate = new Date().toDateString();
    const run = () => {
      void api
        .ensureRecurringToday?.()
        .then((created) => {
          if (created > 0) void loadTasks();
        })
        .catch(() => {});
    };
    run();
    const id = window.setInterval(() => {
      const d = new Date().toDateString();
      if (d !== lastDate) {
        lastDate = d;
        run();
        void loadKpi(); // kpiStore の「今日」をリセット
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [loadTasks, loadKpi]);

  // 分類の先読み: activeTasks のキーが変わるたび debounce 1.5 秒で裏実行。
  // マウント時(起動時 warmup の後)にも1回走る。ビュー切替に依存しない最上位駆動。
  const activeTasksRef = useRef(activeTasks);
  activeTasksRef.current = activeTasks;
  useEffect(() => {
    const id = window.setTimeout(() => {
      // 裏処理: ローディングを出さず、エラーは握りつぶす(関数内で catch 済み)
      void prefetchClassification(activeTasksRef.current);
    }, 1500);
    return () => window.clearTimeout(id);
  }, [prefetchKey]);

  return (
    <div className="flex h-full w-full bg-bg text-text dark:bg-bg-dark dark:text-text-dark">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar commandMode={appView === "command"} />
      </div>

      {/* Mobile drawer (off-canvas sidebar) */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} commandMode={appView === "command"}>
        <Sidebar commandMode={appView === "command"} />
      </MobileDrawer>

      <main className={`flex min-w-0 flex-1 flex-col ${appView === "tasks" ? "pb-16" : "pb-0"} md:pb-0`}>
        {appView === "command" ? (
          <CommandCenter onMenuClick={() => setDrawerOpen(true)} />
        ) : appView === "lanes" ? (
          <LaneBoard onMenuClick={() => setDrawerOpen(true)} />
        ) : appView === "goals" ? (
          <GoalsView onMenuClick={() => setDrawerOpen(true)} />
        ) : appView === "knowledge" ? (
          <KnowledgeView onMenuClick={() => setDrawerOpen(true)} />
        ) : appView === "calendar" ? (
          <CalendarView onMenuClick={() => setDrawerOpen(true)} />
        ) : appView === "inbox" ? (
          <InboxView onMenuClick={() => setDrawerOpen(true)} />
        ) : (
          <>
            {/* Mobile header (hamburger + filter + add) */}
            <MobileHeader onMenuClick={() => setDrawerOpen(true)} />
            {/* Desktop header (KPI cards) */}
            <div className="hidden md:block">
              <TopHeader />
            </div>
            <SearchBox />
            <div className="hidden md:block">
              <FilterBar />
            </div>
            <TaskList />
            <div className="hidden md:block">
              <StatusBar />
            </div>
          </>
        )}
      </main>

      {/* Mobile bottom tab bar */}
      {appView === "tasks" && <BottomTabBar />}

      {/* iOS Add-to-Home Screen banner (mobile, first visit only) */}
      <AddToHomeBanner />

      <TaskEditorModal />
      <GlobalSearchPanel />
      <SettingsModal />
      <VoiceCaptureSheet
        open={useUiStore((s) => s.voiceOpen)}
        onClose={() => useUiStore.getState().setVoiceOpen(false)}
      />
      <ShortcutHintOverlay />
      <Toast
        text={toast}
        actionLabel="元に戻す"
        onAction={() => { void undoDelete(); showToast(null); }}
        onDismiss={() => showToast(null)}
      />
    </div>
  );
}
